import { useEffect, useMemo, useState } from "react";

import {
  ArrowButton,
  Select,
  TableMetadata,
  useTable,
  AutocompleteMultipleSelect,
  Label,
  ColumnSortEnum,
} from "@king-ict/eupisi-ui/components";
import { PagedResult, CodeBook } from "@king-ict/eupisi-ui/models";
import {
  codeBookToSelectData,
  hrDate,
  nameof,
} from "@king-ict/eupisi-ui/utils";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import { searchInstitutionCommittee } from "../../api/Committee/InstitutionCommitteeApi";
import { searchTowns } from "../../api/Geography/GeographyApi";
import {
  ContentArea,
  ToolbarTop,
  SearchCard,
  InstitutionSelect,
  ListTable,
} from "../../components";
import { useEnumCodeBooks } from "../../hooks";
import { useAuth } from "../../hooks/auth/useAuth";
import {
  CommitteeSearch,
  CommitteeSort,
  CommitteeTypeEnum,
  InstitutionCommittee,
} from "../../models/Committee";
import { GrandParentCodeBookPagedFilter } from "../../models/Shared";
import { ClaimType } from "../../models/User";
import { committeeApprovalDetailUrl } from "../../routes/routes";
import { FormatUtils, TableUtils } from "../../utils";

const storageKey = "CommitteeApprovalSearchTableState";

export function CommitteeApprovalList() {
  const { t } = useTranslation();
  const history = useHistory();
  const auth = useAuth();
  //INIT TABLE STATE WITH FILTERS - could be temporary stored in local session storage
  const INITIAL_TABLE_STATE = useMemo(
    () =>
      TableUtils.CreateInitialTableState<CommitteeSearch>(
        storageKey,
        TableUtils.tableDefaultState(CommitteeSort.Code, ColumnSortEnum.DESC)
      ),
    []
  );
  //INIT SEARCH FILTER
  const INITIAL_COMMITTEE_SEARCH = useMemo(
    (): CommitteeSearch => ({
      statusId: INITIAL_TABLE_STATE.statusId,
      institutionId: INITIAL_TABLE_STATE.institutionId,
      institutionTownId: INITIAL_TABLE_STATE.institutionTownId,
      committeeTypeId: CommitteeTypeEnum.Institution,
    }),
    [INITIAL_TABLE_STATE]
  );
  //TABLE DATA
  const defaultTableData = TableUtils.tableDefaultData<InstitutionCommittee>();
  const [tableData, setTableData] =
    useState<PagedResult<InstitutionCommittee>>(defaultTableData);
  //SET SEARCH FILTERS
  const [expertCommissionSearch, setExpertCommissionSearch] =
    useState<CommitteeSearch>(INITIAL_COMMITTEE_SEARCH);
  const [tableSearch, setTableSearch] = useState<CommitteeSearch>(
    INITIAL_COMMITTEE_SEARCH
  );
  const [filterChanged, setFilterChanged] = useState(false);
  const [clearFilter, setClearFilter] = useState(true);
  //TABLE METADATA
  const tableMetadata: TableMetadata<InstitutionCommittee> = {
    rowKey: "id",
    columns: [
      {
        columnKey: "code",
        header: t("common.institutionList.programCode"),
        accessor: (item: InstitutionCommittee) => {
          return <div>{item.institution?.code}</div>;
        },
        sortBy: CommitteeSort.Code,
      },
      {
        columnKey: "town",
        header: t("common.institutionList.townName"),
        accessor: (item: InstitutionCommittee) => {
          return (
            <div>
              {item?.institution?.address
                ? item?.institution?.address.town?.name
                : ""}
            </div>
          );
        },
        sortBy: CommitteeSort.Town,
      },
      {
        columnKey: "name",
        header: t("common.institutionList.institutionName"),
        accessor: (item: InstitutionCommittee) => {
          return <div>{item.institution?.name}</div>;
        },
        sortBy: CommitteeSort.Name,
      },
      {
        columnKey: "dateFromDateTo",
        header: t("common.expertCommission.dateFromDateTo"),
        accessor: (item: InstitutionCommittee) => {
          return (
            <div>
              {item.dateFrom ? hrDate(new Date(item.dateFrom)) : ""} -{" "}
              {item.dateTo ? hrDate(new Date(item.dateTo)) : ""}
            </div>
          );
        },
        sortBy: CommitteeSort.DateFrom,
      },
      {
        columnKey: "status",
        header: t("common.status"),
        accessor: (item: InstitutionCommittee) => {
          return (
            <Label
              color={FormatUtils.getCommitteeStatusColor(item.statusId)}
              className="text-center"
              outline={false}
              fullWidth={false}
            >
              {statuses.find((x) => x.id === item.statusId)?.name}
            </Label>
          );
        },
        sortBy: CommitteeSort.Status,
      },
      {
        columnKey: "details",
        header: "",
        accessor: (item: InstitutionCommittee) => {
          return (
            <div className={"text-right"}>
              <ArrowButton
                ariaLabel={t("common.details")}
                onClick={() => handleDetailsClicked(item.id)}
              />
            </div>
          );
        },
      },
    ],
    rowToolTip: () => undefined,
  };
  //SET TABLE
  const [tableProps, tableState, setFilters] = useTable<CommitteeSearch>({
    metadata: tableMetadata,
    data: tableData!.results,
    pagination: true,
    paginationPage: INITIAL_TABLE_STATE.page,
    paginationPageSize: INITIAL_TABLE_STATE.pageSize as any,
    totalItems: tableData!.rowCount,
    defaultSort: {
      order: INITIAL_TABLE_STATE.order,
      sortBy: INITIAL_TABLE_STATE.sortBy,
    },
  });
  //FILTER DATA
  const { committeeStatusCodeBook: statuses } = useEnumCodeBooks();
  const [filteredTowns, setFilteredTowns] = useState<CodeBook[]>([]);

  useEffect(() => {
    (async () => {
      if (expertCommissionSearch !== null) {
        if (
          expertCommissionSearch.institutionTownId &&
          filteredTowns.length === 0
        ) {
          const searchTown: GrandParentCodeBookPagedFilter = {
            pageSize: 500,
            page: 1,
            ids: expertCommissionSearch.institutionTownId,
            search: null,
          };
          const result = await searchTowns(searchTown);
          if (result && result.results) setFilteredTowns(result.results);
        }
        sessionStorage.removeItem(storageKey);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (clearFilter) {
      setClearFilter(false);
      setFilterChanged(false);
    } else setFilterChanged(true);
    TableUtils.RemoveSessionStorageItem(storageKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expertCommissionSearch]);

  useEffect(() => {
    (async () => {
      const request = TableUtils.GetSearchRequest<CommitteeSearch>(
        tableSearch,
        tableState
      );
      const itemList = await searchInstitutionCommittee(request);
      if (itemList !== null && itemList.results.length > 0) {
        setTableData(itemList);
      } else {
        setTableData(defaultTableData);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableState]);

  const handleDetailsClicked = (id?: number) => {
    if (id) {
      TableUtils.SetSessionStorageItem<CommitteeSearch>(
        storageKey,
        tableSearch,
        tableState
      );
      history.push(committeeApprovalDetailUrl(id!));
    }
  };
  const search = () => {
    setFilters({ ...tableSearch });
  };

  const handleTownChange = (e: any) => {
    const townList = e.target.value as CodeBook[];
    // If we change the towns, make sure the institution is still valid.
    const newExpertCommissionSearch: CommitteeSearch = {
      ...expertCommissionSearch,
      institutionTownId:
        townList.length > 0 ? townList.map((val) => val.id) : undefined,
    };

    setExpertCommissionSearch(newExpertCommissionSearch);
  };
  const handleInstitutionChange = (val: number) => {
    const newExpertCommissionSearch: CommitteeSearch = {
      ...expertCommissionSearch,
      institutionId: !isNaN(val) ? val : undefined,
    };
    setExpertCommissionSearch(newExpertCommissionSearch);
  };
  const handleStatusChange = (e: any) => {
    const status = Number.parseInt(e.target.value);
    const newExpertCommissionSearch: CommitteeSearch = {
      ...expertCommissionSearch,
      statusId: !isNaN(status) ? status : undefined,
    };
    setExpertCommissionSearch(newExpertCommissionSearch);
  };
  const handleTownFilterChange = async (filter: string) => {
    const searchTown: GrandParentCodeBookPagedFilter = {
      pageSize: 500,
      page: 1,
      ids: expertCommissionSearch.institutionTownId,
      grandParentId:
        auth.currentRole?.claimType === ClaimType.County
          ? auth.currentRole.locations
          : undefined,
      search: filter,
    };
    const result = await searchTowns(searchTown);
    if (result && result.results) setFilteredTowns(result.results);
  };

  return (
    <ContentArea>
      <ToolbarTop
        showAdd={"hide"}
        showBack={"hide"}
        pageTitle={t("common.expertCommission.pageName")}
      />
      <SearchCard
        onClickSearch={() => {
          setTableSearch(expertCommissionSearch);
          setFilterChanged(false);
          search();
        }}
        onClickClearSearch={() => {
          setClearFilter(true);
          setExpertCommissionSearch({
            committeeTypeId: CommitteeTypeEnum.Institution,
          });
          setTableSearch({ committeeTypeId: CommitteeTypeEnum.Institution });
          search();
        }}
        isFilterChanged={filterChanged}
      >
        <Row>
          <Col md={4}>
            <AutocompleteMultipleSelect<CodeBook>
              label={t("common.institutionList.townName")}
              name={nameof<typeof expertCommissionSearch>("institutionTownId")}
              value={
                expertCommissionSearch.institutionTownId !== undefined
                  ? filteredTowns.filter(
                      (val) =>
                        expertCommissionSearch.institutionTownId!.find(
                          (v) => v === val.id
                        ) !== undefined
                    )
                  : []
              }
              valueProp="id"
              textProp="name"
              onChange={handleTownChange}
              selectOptions={filteredTowns}
              onFilterChange={handleTownFilterChange}
            />
          </Col>
          <Col md={4}>
            <InstitutionSelect
              label={t("common.schoolCandidateModel.institutionList")}
              name={nameof<typeof expertCommissionSearch>("institutionId")}
              countyIds={
                auth.currentRole?.claimType === ClaimType.County
                  ? auth.currentRole.locations
                  : undefined
              }
              townIds={expertCommissionSearch.institutionTownId}
              ids={
                auth.currentRole?.claimType === ClaimType.Institution
                  ? auth.currentRole.locations
                  : undefined
              }
              onlyByEnrollmentArea={false}
              excludeHandicapped={false}
              excludePrivate={false}
              excludeSatellite={true}
              value={expertCommissionSearch.institutionId}
              onChange={(e) =>
                handleInstitutionChange(Number.parseInt(e.target.value))
              }
            />
          </Col>
          <Col md={4}>
            <Select
              label={t("common.schoolCandidateModel.status")}
              name={nameof<typeof expertCommissionSearch>("statusId")}
              value={expertCommissionSearch.statusId}
              onChange={handleStatusChange}
              selectData={codeBookToSelectData(statuses)}
              emptyOption={{ value: undefined, text: t("common.all") }}
            />
          </Col>
        </Row>
      </SearchCard>
      <ListTable tableProps={tableProps} />
    </ContentArea>
  );
}
